<script setup>
import { v4 as uuidv4 } from 'uuid'
import { computed, watch, ref, watchEffect, onMounted } from 'vue'

import EventTables from '@/components/EventTables.vue'
import EventParticipates from '@/components/EventParticipates.vue'
import EventPreferredPairings from '@/components/EventPreferredPairings.vue'
import EventSettings from '@/components/EventSettings.vue'

import whatsNew from '@/whats-new.js'
import { useAssignment } from '@/composables/useAssignment.js'

import { usePairingStore } from '@/stores/PairingStore.js'
import { useParticipateStore } from '@/stores/ParticipateStore.js'
import { useSettingsStore } from '@/stores/SettingsStore.js'
import { useTableStore } from '@/stores/TableStore.js'

const { assignAllParticipates } = useAssignment()

const pairingStore = usePairingStore()
const participateStore = useParticipateStore()
const settingsStore = useSettingsStore()
const tableStore = useTableStore()

const app_name = __APP_NAME__
const app_version = __APP_VERSION__

const announcement = {
    show: __ANNOUNCEMENT_SHOW__,
    message: __ANNOUNCEMENT_MESSAGE__
}

document.title = app_name

// Reactive state to track modal visibility
const isModalOpen = ref(false);

const handleModalShown = () => {
  isModalOpen.value = true
}

const handleModalHidden = () => {
  isModalOpen.value = false
}

document.addEventListener('shown.bs.modal', handleModalShown)
document.addEventListener('hidden.bs.modal', handleModalHidden)


watchEffect(() => {
    let theme = settingsStore.darkModeToggle ? 'dark' : 'light'
    document.documentElement.setAttribute('data-bs-theme', theme)
})


const columnSizeOptions = [
    {value: 0, text: 'Auto'},
    {value: 1, text: '1'},
    {value: 2, text: '2'},
    {value: 3, text: '3'},
    {value: 4, text: '4'},
    {value: 5, text: '5'},
    {value: 6, text: '6'}
]

const fontSizeOptions = [
    {value: 6, text: 'Regular'},
    {value: 5, text: 'Large'},
    {value: 4, text: 'Extra Large'}
]

document.body.addEventListener("keydown", (ev) => {
    if (isModalOpen.value) {
        return
    }

    const ignoreIfElementTypesActive = [
        'HTMLInputElement',
        'HTMLTextAreaElement'
    ]

    if (ignoreIfElementTypesActive.includes(document.activeElement.constructor.name)) {
        return
    }

    if (ev.metaKey || ev.ctrlKey || ev.altKey) {
        return
    }

    if (ev.key === 'h') {
        settingsStore.toggleOptions()
    }

    if (ev.key === 'r') {
        assignAllParticipates(true)
    }
})

const now = new Date()

const LAST_SEEN_VERSION_KEY = 'cardinate_last_seen_version'
const whatsNewForVersion = whatsNew.find(r => r.version === app_version) ?? null
const showWhatsNewModal = ref(false)

onMounted(() => {
    const lastSeen = localStorage.getItem(LAST_SEEN_VERSION_KEY)
    if (lastSeen !== app_version && whatsNewForVersion) {
        showWhatsNewModal.value = true
    }
})

function dismissWhatsNew() {
    localStorage.setItem(LAST_SEEN_VERSION_KEY, app_version)
    showWhatsNewModal.value = false
}
</script>

<template>
<div class="row m-0 h-100">
    <div class="m-0 p-0 h-md-100" :class="{'col-lg-9 col-md-8': settingsStore.showOptions}" style="overflow-y: auto">
        <div class="p-3">
            <div class="card">
                <div class="card-header w-100">
                    <div class="hstack gap-2">
                        <div class="me-auto">
                            <strong>Tables ({{ tableStore.tables.length }})</strong>
                        </div>

                        <div class="mx-1 d-none d-lg-block">
                            <div class="row g-3 align-items-center">
                                <div class="col-auto">
                                    <label for="columnsSelection" class="col-form-label">Columns:</label>
                                </div>
                                <select class="form-select form-select-sm" style="width: auto;" id="columnsSelection" aria-label="Small select example" v-model="settingsStore.numberOfColumns">
                                    <option v-for="option in columnSizeOptions" :key="option.value" :value="option.value">
                                        {{ option.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="mx-1 d-none d-lg-block">
                            <div class="row g-3 align-items-center">
                                <div class="col-auto">
                                    <label for="fontSizeSelection" class="col-form-label">Font:</label>
                                </div>
                                <select class="form-select form-select-sm" style="width: auto;" id="fontSizeSelection" aria-label="Small select example" v-model="settingsStore.fontSize">
                                    <option v-for="option in fontSizeOptions" :key="option.value" :value="option.value">
                                        {{ option.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="ms-1">
                            <button type="button" class="btn btn-primary btn-sm" @click="settingsStore.toggleOptions">
                                {{settingsStore.showOptions ? 'Hide Options' : 'Show Options'}}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="card-body">
                    <EventTables></EventTables>
                </div>
            </div>
        </div>
    </div>

    <div v-if="settingsStore.showOptions == true" class="col-lg-3 col-md-4 m-0 p-0 h-md-100" style="overflow-y: auto">
        <div class="p-3">
            <div v-if="announcement.show == 'true' && announcement.message != ''" class="alert alert-warning alert-dismissible fade show" role="alert">
                <span v-html="announcement.message"></span>
                <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
            </div>
            <EventParticipates></EventParticipates>
            <EventPreferredPairings></EventPreferredPairings>
            <EventSettings @show-whats-new="showWhatsNewModal = true"></EventSettings>
            <div class="text-center text-body-secondary">
                Constructed by DarkNova.co &middot; <a href='https://status.darknova.co/796789059' target='_blank'>Status</a>
                <br/>
                {{app_version}} - &copy; {{now.getFullYear()}}
            </div>
        </div>
    </div>
</div>

<!-- What's New Modal -->
<div v-if="showWhatsNewModal && whatsNewForVersion"
     class="modal"
     style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1055; display: flex; align-items: center; justify-content: center;"
     tabindex="-1">
    <div class="modal-dialog" style="margin: 0;">
        <div class="modal-content">
            <div class="modal-header">
                <h1 class="modal-title fs-5">What's New in {{ app_version }}</h1>
                <button type="button" class="btn-close" @click="dismissWhatsNew" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                <template v-if="whatsNewForVersion.features?.length">
                    <h6 class="text-uppercase fw-bold mb-2" style="font-size: 0.7rem; letter-spacing: 0.08em;">Features</h6>
                    <ul :class="whatsNewForVersion.fixes?.length ? 'mb-3' : 'mb-0'">
                        <li v-for="feature in whatsNewForVersion.features" :key="feature">{{ feature }}</li>
                    </ul>
                </template>
                <template v-if="whatsNewForVersion.fixes?.length">
                    <h6 class="text-uppercase fw-bold mb-2" style="font-size: 0.7rem; letter-spacing: 0.08em;">Fixes</h6>
                    <ul class="mb-0">
                        <li v-for="fix in whatsNewForVersion.fixes" :key="fix">{{ fix }}</li>
                    </ul>
                </template>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-primary" @click="dismissWhatsNew">Got it!</button>
            </div>
        </div>
    </div>
</div>
</template>

<style scoped>

@media (min-width: 768px) {
    .h-md-100 {
        height: 100% !important;
    }
}

</style>
